import { useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Flex, Box, TextArea, Card, Tooltip } from "@radix-ui/themes";
import { Pencil2Icon, CheckIcon, Cross1Icon } from "@radix-ui/react-icons";
import { Message } from "@/components/quack-chat/core/entities/Message";
import { FeatureAccessContext } from "@/context/featureAccess";
import styles from "./QuackchatSystemMessageConfiguration.module.scss";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import {
  QuackChatSystemMessageType,
  quackChatSystemMessageTypesMap,
} from "@/v2/entities/quackchat/Quackchat.model";
import { useQuackchatApi } from "@/v2/entities/quackchat/Quackchat.api";
import { useMutation } from "react-query";
import { toast } from "sonner";

interface EditSystemMessageProps {
  type: QuackChatSystemMessageType;
  message: Message;
  isEditing: boolean;
  simulatorId: string | undefined;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onSuccess: () => void;
}

export function EditSystemMessage({
  type,
  message,
  isEditing,
  simulatorId,
  onStartEdit,
  onStopEdit,
  onSuccess,
}: EditSystemMessageProps) {
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const quackchatApi = useQuackchatApi();

  const { mutate: overrideSystemMessage, isLoading } = useMutation({
    mutationKey: ["quackchat-system-message", type, selectedWorkspace],
    mutationFn: (message: string) =>
      quackchatApi.overrideSystemMessage({
        tenant: selectedWorkspace,
        type,
        message,
        simulatorId,
      }),
  });

  const { data: configuration, update: updateConfiguration } =
    useQuackchatPlatformConfiguration(simulatorId);

  const [elem, setElem] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState(message.text);

  const insertVariable = useCallback((variable: string) => {
    setValue((prev) => prev + variable);
  }, []);

  const handleEdit = useCallback(() => {
    onStartEdit();
  }, [onStartEdit]);

  const handleCancel = useCallback(() => {
    onStopEdit();
    setValue(message.text);
  }, [message.text, onStopEdit]);

  const updateErrorFallbackContent = useCallback(
    (value: string) => {
      if (configuration == null) {
        return;
      }

      updateConfiguration({
        ...configuration,
        chatSettings: {
          ...configuration.chatSettings,
          errorFallbackContent: value,
        },
      });
    },
    [configuration, updateConfiguration]
  );

  const hadleSave = useCallback(async () => {
    if (value === message.text) {
      onStopEdit();
      return;
    }

    overrideSystemMessage(value, {
      onSuccess: () => {
        if (type === "FALLBACK_ERROR") {
          updateErrorFallbackContent(value);
        }

        onSuccess();
        onStopEdit();

        const title = quackChatSystemMessageTypesMap[type].title;
        toast.success(`${title} updated`);
      },
      onError: () => {
        const title = quackChatSystemMessageTypesMap[type].title;
        toast.error(`Failed to update ${title}`);
      },
    });
  }, [
    selectedWorkspace,
    type,
    value,
    overrideSystemMessage,
    onSuccess,
    updateErrorFallbackContent,
  ]);

  useEffect(() => {
    setElem(document.getElementById(message.deduplicationKey));
  }, [message.deduplicationKey]);

  useEffect(() => {
    setValue(message.text);
  }, [message.text]);

  if (elem == null) {
    return <></>;
  }

  if (isEditing) {
    return createPortal(
      <Flex direction={"column"} gap={"2"} className={styles.EditSystemMessage}>
        <Card variant="surface" style={{ padding: 4 }}>
          <TextArea
            variant={"soft"}
            color={"gray"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={"Enter message..."}
          />
        </Card>

        <Flex justify={"between"} align={"center"} gap={"2"}>
          {type === "CREATED_TICKET" && (
            <Tooltip content="This will be replaced with the actual ticket ID">
              <Button
                size={"2"}
                color={"blue"}
                variant={"soft"}
                onClick={() => insertVariable("{ticket_id}")}
              >
                + {"{ticket_id}"}
              </Button>
            </Tooltip>
          )}

          <Flex justify={"end"} align={"center"} gap={"2"}>
            <Button
              size={"2"}
              color={"gray"}
              variant={"outline"}
              style={{
                boxShadow: "var(--shadow-3)",
              }}
              onClick={handleCancel}
            >
              <Cross1Icon />
              Cancel
            </Button>

            <Button
              size={"2"}
              color={"gray"}
              variant={"solid"}
              highContrast
              disabled={isLoading}
              style={{
                boxShadow: "var(--shadow-3)",
              }}
              onClick={hadleSave}
            >
              <CheckIcon />
              Save
            </Button>
          </Flex>
        </Flex>
      </Flex>,
      elem
    );
  }

  return createPortal(
    <Box
      style={{
        position: "absolute",
        bottom: -66,
        left: 8,
      }}
    >
      <Button
        size={"2"}
        color={"gray"}
        variant={"outline"}
        style={{
          boxShadow: "var(--shadow-3)",
        }}
        onClick={handleEdit}
      >
        <Pencil2Icon />
        Edit
      </Button>
    </Box>,
    elem
  );
}
